import React, { useState } from 'react'
import { TrendingUp, TrendingDown, Minus, ChevronRight, ChevronDown, Target, AlertTriangle, Info } from 'lucide-react'
import { CausalChainNode } from '../types'

interface CausalChainAnalysisProps {
  result: CausalChainNode | null
  isLoading: boolean
}

const CausalChainNodeComponent: React.FC<{ 
  node: CausalChainNode; 
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  expandedNodes: Set<string>;
  toggleNode: (nodeId: string) => void;
}> = ({ node, level, isExpanded, onToggle, expandedNodes, toggleNode }) => {
  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'positive':
        return <TrendingUp className="w-5 h-5 text-green-600" />
      case 'negative':
        return <TrendingDown className="w-5 h-5 text-red-600" />
      default:
        return <Minus className="w-5 h-5 text-gray-600" />
    }
  }

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'positive':
        return 'border-green-300 bg-green-50 hover:bg-green-100'
      case 'negative':
        return 'border-red-300 bg-red-50 hover:bg-red-100'
      default:
        return 'border-gray-300 bg-gray-50 hover:bg-gray-100'
    }
  }

  const getImpactText = (impact: string) => {
    switch (impact) {
      case 'positive':
        return 'Positive Impact'
      case 'negative':
        return 'Negative Impact'
      default:
        return 'Neutral Impact'
    }
  }

  const getProbabilityColor = (probability: number) => {
    if (probability >= 0.7) return 'text-green-600 bg-green-100'
    if (probability >= 0.4) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getProbabilityText = (probability: number) => {
    if (probability >= 0.7) return 'High'
    if (probability >= 0.4) return 'Medium'
    return 'Low'
  }

  return (
    <div className="space-y-4">
      <div 
        className={`p-6 border-2 rounded-xl transition-all duration-200 cursor-pointer ${getImpactColor(node.impact)}`}
        onClick={onToggle}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-sm">
                <Target className="w-4 h-4 text-gray-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{node.title}</h4>
            </div>
            <p className="text-gray-700 leading-relaxed">{node.description}</p>
          </div>
          <div className="flex items-center space-x-3">
            {getImpactIcon(node.impact)}
            <div className="text-right">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getProbabilityColor(node.probability)}`}>
                {getProbabilityText(node.probability)} Probability
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {Math.round(node.probability * 100)}% chance
              </div>
            </div>
            {node.children && node.children.length > 0 && (
              <div className="ml-2">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-600">
              Level {node.level} Outcome
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              node.impact === 'positive' ? 'bg-green-100 text-green-800' :
              node.impact === 'negative' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {getImpactText(node.impact)}
            </span>
          </div>
          {node.children && node.children.length > 0 && (
            <div className="text-sm text-gray-500">
              {node.children.length} sub-outcome{node.children.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
      
      {node.children && node.children.length > 0 && isExpanded && (
        <div className="ml-8 space-y-4 relative">
          {/* Connection line */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-300 transform -translate-x-4" />
          
          {node.children.map((child) => (
            <div key={child.id} className="relative">
              {/* Horizontal connection line */}
              <div className="absolute left-0 top-6 w-4 h-0.5 bg-gray-300 transform -translate-x-4" />
              <CausalChainNodeComponent 
                node={child} 
                level={level + 1} 
                isExpanded={expandedNodes.has(child.id)}
                onToggle={() => toggleNode(child.id)}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const CausalChainAnalysis: React.FC<CausalChainAnalysisProps> = ({
  result,
  isLoading
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

  // Add comprehensive logging
  console.log('=== CausalChainAnalysis Component Debug ===')
  console.log('Raw result:', result)
  console.log('Result type:', typeof result)
  console.log('Is loading:', isLoading)

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpandedNodes(newExpanded)
  }

  // Validate and parse result data
  const validateAndParseResult = (data: any): CausalChainNode | null => {
    console.log('=== validateAndParseResult called ===')
    console.log('Input data:', data)
    console.log('Input data type:', typeof data)
    
    // If data is already a valid CausalChainNode, return it
    if (data && typeof data === 'object' && data.id && data.title && data.description !== undefined) {
      console.log('Data is already a valid CausalChainNode')
      
      // Check if description contains JSON that should be parsed
      if (data.description && typeof data.description === 'string' && data.description.includes('```json')) {
        console.log('Description contains JSON code block, attempting to extract')
        try {
          // Extract JSON from code block - fix the regex pattern
          const jsonMatch = data.description.match(/```json\s*([\s\S]*?)\s*```/)
          if (jsonMatch) {
            console.log('Found JSON code block in description')
            console.log('Extracted JSON string:', jsonMatch[1])
            const extractedJson = JSON.parse(jsonMatch[1])
            console.log('Parsed extracted JSON:', extractedJson)
            
            // Validate the extracted JSON
            if (extractedJson && typeof extractedJson === 'object' && extractedJson.id && extractedJson.title) {
              console.log('Extracted JSON is valid, using it instead')
              return extractedJson
            }
          } else {
            console.log('No JSON match found in code block')
            console.log('Description content:', data.description)
          }
        } catch (parseError) {
          console.error('Failed to parse JSON from description:', parseError)
          console.log('Raw JSON string that failed to parse:', data.description.match(/```json\s*([\s\S]*?)\s*```/)?.[1])
        }
      }
      
      // Check if description itself is a JSON string
      if (data.description && typeof data.description === 'string' && data.description.trim().startsWith('{')) {
        console.log('Description appears to be a JSON string, attempting to parse')
        try {
          const parsedDescription = JSON.parse(data.description)
          console.log('Parsed description JSON:', parsedDescription)
          
          // Validate the parsed JSON
          if (parsedDescription && typeof parsedDescription === 'object' && parsedDescription.id && parsedDescription.title) {
            console.log('Parsed description JSON is valid, using it instead')
            return parsedDescription
          }
        } catch (parseError) {
          console.error('Failed to parse description as JSON:', parseError)
          
          // Try to fix common JSON issues
          console.log('Attempting to fix JSON parsing issues...')
          try {
            let fixedJson = data.description
            
            // Try to find the last complete object by looking for balanced braces
            let braceCount = 0
            let lastCompletePosition = -1
            
            for (let i = 0; i < fixedJson.length; i++) {
              if (fixedJson[i] === '{') {
                braceCount++
              } else if (fixedJson[i] === '}') {
                braceCount--
                if (braceCount === 0) {
                  lastCompletePosition = i
                }
              }
            }
            
            if (lastCompletePosition > 0) {
              console.log('Found complete JSON object at position:', lastCompletePosition)
              fixedJson = fixedJson.substring(0, lastCompletePosition + 1)
              const parsedFixed = JSON.parse(fixedJson)
              
              if (parsedFixed && typeof parsedFixed === 'object' && parsedFixed.id && parsedFixed.title) {
                console.log('Successfully parsed fixed JSON:', parsedFixed)
                return parsedFixed
              }
            }
          } catch (fixError) {
            console.error('Failed to fix JSON:', fixError)
          }
        }
      }
      
      return data
    }

    // If data is a string, try to parse it as JSON
    if (typeof data === 'string') {
      console.log('Data is a string, attempting to parse JSON')
      try {
        // Try to extract JSON from the string (in case it contains explanatory text)
        const jsonMatch = data.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          console.log('Found JSON match in string')
          const parsed = JSON.parse(jsonMatch[0])
          console.log('Parsed JSON:', parsed)
          if (parsed && typeof parsed === 'object' && parsed.id && parsed.title) {
            console.log('Parsed JSON is valid CausalChainNode')
            return parsed
          }
        }
      } catch (error) {
        console.error('Failed to parse result data:', error)
      }
    }

    // If data is an object but not in the expected format, try to find the analysis property
    if (data && typeof data === 'object' && data.analysis) {
      console.log('Found analysis property in data object')
      return validateAndParseResult(data.analysis)
    }

    console.log('No valid data found, returning null')
    return null
  }

  const parsedResult = validateAndParseResult(result)
  console.log('Final parsed result:', parsedResult)
  
  // Add detailed inspection of the parsed result
  if (parsedResult) {
    console.log('=== Detailed Result Inspection ===')
    console.log('Title:', parsedResult.title)
    console.log('Description:', parsedResult.description)
    console.log('Description length:', parsedResult.description?.length)
    console.log('Level:', parsedResult.level)
    console.log('Probability:', parsedResult.probability)
    console.log('Impact:', parsedResult.impact)
    console.log('Children count:', parsedResult.children?.length)
    console.log('Children:', parsedResult.children)
    
    // Check if description contains JSON or explanatory text
    if (parsedResult.description && typeof parsedResult.description === 'string') {
      console.log('Description contains "JSON":', parsedResult.description.includes('JSON'))
      console.log('Description contains "format":', parsedResult.description.includes('format'))
      console.log('Description contains "Here\'s":', parsedResult.description.includes("Here's"))
    }
  }

  if (isLoading) {
    console.log('Rendering loading state')
    return (
      <div className="space-y-8">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <h3 className="text-xl font-semibold text-blue-900">Analyzing Causal Chain</h3>
          </div>
          <p className="text-blue-800 leading-relaxed">
            Our AI is mapping out the potential outcomes of your decision, showing how each choice leads to different results and their likelihood of occurring.
          </p>
        </div>
        
        <div className="space-y-6">
          {[1, 2, 3].map((level) => (
            <div key={level} className="animate-pulse">
              <div className="h-32 bg-gray-200 rounded-xl"></div>
              {level < 3 && (
                <div className="ml-8 mt-6 space-y-4">
                  <div className="h-28 bg-gray-200 rounded-xl"></div>
                  <div className="h-28 bg-gray-200 rounded-xl"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!parsedResult) {
    console.log('Rendering error state - no valid parsed result')
    return (
      <div className="space-y-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h3 className="text-xl font-semibold text-red-900">Analysis Result Format Error</h3>
          </div>
          <p className="text-red-800 leading-relaxed mb-4">
            The analysis result could not be properly formatted. This might be due to an unexpected response format from the AI service.
          </p>
          
          {result && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Raw Response:</h4>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-auto max-h-64">
                {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
          
          <div className="mt-4">
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  console.log('Rendering success state with parsed result')
  
  // Calculate summary statistics
  const totalNodes = countNodes(parsedResult)
  const positiveNodes = countNodesByImpact(parsedResult, 'positive')
  const negativeNodes = countNodesByImpact(parsedResult, 'negative')
  const neutralNodes = countNodesByImpact(parsedResult, 'neutral')
  const avgProbability = calculateAverageProbability(parsedResult)

  console.log('Summary statistics:', { totalNodes, positiveNodes, negativeNodes, neutralNodes, avgProbability })

  return (
    <div className="space-y-8">
      {/* Success Message */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
            <Target className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-900">Causal Chain Analysis Complete</h3>
        </div>
        <p className="text-green-800 leading-relaxed">
          Here's how your decision creates a chain of outcomes. Each level shows the potential results and their likelihood of occurring.
        </p>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{totalNodes}</div>
          <div className="text-sm text-gray-600">Total Outcomes</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{positiveNodes}</div>
          <div className="text-sm text-gray-600">Positive Impacts</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{negativeNodes}</div>
          <div className="text-sm text-gray-600">Negative Impacts</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{Math.round(avgProbability * 100)}%</div>
          <div className="text-sm text-gray-600">Avg Probability</div>
        </div>
      </div>

      {/* Main Causal Chain */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3 mb-6">
          <h4 className="text-lg font-semibold text-gray-900">Decision Outcome Chain</h4>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Info className="w-4 h-4" />
            <span>Click on outcomes to expand sub-outcomes</span>
          </div>
        </div>
        
        <CausalChainNodeComponent 
          node={parsedResult} 
          level={1} 
          isExpanded={expandedNodes.has(parsedResult.id)}
          onToggle={() => toggleNode(parsedResult.id)}
          expandedNodes={expandedNodes}
          toggleNode={toggleNode}
        />
      </div>

      {/* Analysis Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-blue-900">How to Read This Analysis</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">1</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">Outcome Levels</div>
                <div className="text-sm text-blue-800">Each level shows the next step in the chain of consequences</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">2</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">Probability</div>
                <div className="text-sm text-blue-800">Shows how likely each outcome is to occur</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">3</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">Impact Type</div>
                <div className="text-sm text-blue-800">Positive, negative, or neutral effects on your goals</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">4</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">Chain Depth</div>
                <div className="text-sm text-blue-800">Shows up to 3 levels of cause-and-effect relationships</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function countNodes(node: CausalChainNode): number {
  let count = 1
  if (node.children) {
    node.children.forEach(child => {
      count += countNodes(child)
    })
  }
  return count
}

function countNodesByImpact(node: CausalChainNode, impact: string): number {
  let count = node.impact === impact ? 1 : 0
  if (node.children) {
    node.children.forEach(child => {
      count += countNodesByImpact(child, impact)
    })
  }
  return count
}

function calculateAverageProbability(node: CausalChainNode): number {
  let total = node.probability
  let count = 1
  
  if (node.children) {
    node.children.forEach(child => {
      const childResult = calculateAverageProbability(child)
      total += childResult
      count += 1
    })
  }
  
  return total / count
} 